import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, Box, Typography, Button, TextField, Select, MenuItem, IconButton, Autocomplete, Menu, ListItemText, Checkbox, Alert } from '@mui/material';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { RESERVATIONS_URL, RESERVATION_CREATE_URL, RESERVATION_UPDATE_URL, RESERVATION_CANCEL_URL, RESERVATION_ACTIVATE_URL, getGeojsonFile, DATA_URL, agencies } from '../config';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocation } from './AreaContext';
import '../styles/List.css';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import AlertTitle from '@mui/material/AlertTitle';

const ModalStyles = {
  overlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1000,
  },
  modal: {
    position: 'relative',
    width: '90%',
    maxHeight: '92%',
    maxWidth: '800px',
    overflow: 'auto',
    borderRadius: '0px',
    padding: '20px',
    backgroundColor: '#f8f8f8'
  },
  sectionHeader: {
    backgroundColor: '#1a475f',
    color: 'white',
    padding: '10px',
    marginTop: '10px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionContent: {
    marginBottom: '12px',
    border: '1px solid #d9d9d9',
    borderRadius: '2px',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    borderTop: 'none',
    padding: '10px',
  },
  gap: {
    marginBottom: '16px',
  }
};

const ReservationForm = ({ user, view, isOpen, onClose, reservationId, mode, jsonData }) => {
  const [callsigns, setCallsigns] = useState([]);
  const [event, setEvent] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [availableAirspaces, setAvailableAirspaces] = useState([]);
  const [airspaces, setAirspaces] = useState([{ name: '', lower: 20, upper: 95 }]);
  const [priority, setPriority] = useState(99);
  const [numAircraft, setNumAircraft] = useState(1);
  const [remarks, setRemarks] = useState('');
  const [bookingType, setBookingType] = useState('');
  const [agency, setAgency] = useState('');
  const [permeability, setPermeability] = useState('');
  const [activityType, setActivityType] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [callsignError, setCallsignError] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [creator, setCreator] = useState(0);
  const [reservation, setReservation] = useState(null);
  const [loadedPreset, setLoadedPreset] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const { selectedLocation } = useLocation();
  const [showPresetMessage, setShowPresetMessage] = useState(false);
  const [overrideLimits, setOverrideLimits] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [showDraftDialog, setShowDraftDialog] = useState(false);

  // validation settings
  const [maxReservableByOne, setMaxReservableByOne] = useState(2);
  const [maxReservations, setMaxReservations] = useState(10);
  const [maxAreasInOne, setMaxAreasInOne] = useState(25);
  const [forceVerticalLimits, setForceVerticalLimits] = useState(false);
  const [clusterIsActive, setClusterIsActive] = useState(true);

  const [events, setEvents] = useState([]);

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    activity: true,
    time: true,
    airspace: true,
    remarks: true,
  });

  console.log(user);

  useEffect(() => {
    if (showPresetMessage) {
      const timer = setTimeout(() => {
        setShowPresetMessage(false);
      }, 3000); // Hide after 3 seconds
  
      return () => clearTimeout(timer);
    }
  }, [showPresetMessage]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(DATA_URL + 'getAllEvents', { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to fetch events');
        const data = await res.json();
        setEvents(data);
      } catch (err) {
        console.error('Error fetching events:', err);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const fetchAirspaces = async () => {
      try {
        const response = await fetch(getGeojsonFile(selectedLocation));
        if (!response.ok) throw new Error('Failed to load data');
        const geojson = await response.json();
        const airspaces = geojson.features
          .filter(feature => feature.properties.name)
          .map((feature) => ({
            name: feature.properties.name,
            lower: feature.properties.lowerFL || 0,
            upper: feature.properties.upperFL || 660,
            group: feature.properties.group,
            type: feature.properties.type || '',
            mustBeBookedWith: feature.properties.mustBeBookedWith || [],
            activation: feature.properties.activationLimits || [],
            coordinates: feature.geometry.coordinates || [],
            foreignClusters: feature.properties.foreignClusters || [],
          }))
          .sort((a, b) => {
            if (a.type !== b.type) {
              return a.type.localeCompare(b.type);
            }
            return a.name.localeCompare(b.name);
          });
        setAvailableAirspaces(airspaces);
      } catch (error) {
        console.error('Error fetching airspaces:', error);
      }
    };
    fetchAirspaces();
  }, [selectedLocation]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(DATA_URL + `settings/${encodeURIComponent(selectedLocation)}?t=${Date.now()}`, {
          credentials: 'include',
          cache: 'no-store'
        });
        if (response.ok) {
          const data = await response.json();
          setMaxReservableByOne(data.maxReservableByOne);
          setMaxReservations(data.maxReservations);
          setMaxAreasInOne(data.maxAreasInOne);
          setForceVerticalLimits(data.forceVerticalLimits);
          setClusterIsActive(data.clusterIsActive);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, [selectedLocation]);

  useEffect(() => {
    if (mode === 'edit' && availableAirspaces.length > 0) {
        axios.get(`${RESERVATIONS_URL}${selectedLocation}.json`)
        .then((response) => {
          const fetchedReservation = response.data.find(res => res.reservation_id === reservationId);
          if (fetchedReservation) {
            setReservation(fetchedReservation);
            setCallsigns(fetchedReservation.callsigns || []);
            setStartDate(new Date(fetchedReservation.start));
            setEndDate(new Date(fetchedReservation.end));
            const reservationAirspaces = fetchedReservation.areas || [];
            const updatedAirspaces = reservationAirspaces.map((as) => {
              const airspaceExists = availableAirspaces.some(available => available.name === as.area_id);
              const validName = airspaceExists ? as.area_id : 'NIL';
              return {
                name: validName,
                lower: as.lower || 0,
                upper: as.upper || 660,
              };
            });
            setAirspaces(updatedAirspaces);
            setPriority(fetchedReservation.priority || 99);
            setEvent(fetchedReservation.event || '');
            setCreator(fetchedReservation.creator || 0);
            setNumAircraft(fetchedReservation.nbr_acft || 1);
            setRemarks(fetchedReservation.remarks || '');
            setBookingType(fetchedReservation.booking_type || '');
            setAgency(fetchedReservation.agency || '');
            setPermeability(fetchedReservation.permeability || '');
            setActivityType(fetchedReservation.activity_type || '');
          }
        })
        .catch((error) => console.error('Error fetching reservation data:', error));
    }
  }, [mode, reservationId, availableAirspaces]);

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const handleCallsignAdd = (event) => {
    const trimmed = event.target.value.trim().toUpperCase().replace(',', '');
    const validCallsign = /^[A-Z0-9]+$/.test(trimmed);
    if (validCallsign && !callsigns.includes(trimmed)) {
      setCallsigns([...callsigns, trimmed]);
      event.target.value = '';
      setCallsignError('');
    } else if (!validCallsign && trimmed) {
      setCallsignError('Callsign can only contain letters and numbers.');
    }
  };

  const handleCallsignRemove = (callsign) => {
    setCallsigns(callsigns.filter((cs) => cs !== callsign));
  };

  const handleAirspaceAdd = () => {
    setAirspaces([...airspaces, { name: '', lower: 20, upper: 95 }]);
  };

  const handleAirspaceDelete = (id) => {
    if (airspaces.length > 1) {
      setAirspaces(airspaces.filter((_, index) => index !== id));
    }
  };

  const handleAirspaceChange = (id, field, value) => {
    const parsedValue = parseInt(value, 10);
    if (!isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 999) {
      setAirspaces(airspaces.map((as, index) =>
        index === id ? { ...as, [field]: parsedValue } : as
      ));
    }
  };

  const handleAirspaceNameChange = (id, value) => {
    const selectedAirspace = availableAirspaces.find(airspace => airspace.name === value);
    if (selectedAirspace) {
      setAirspaces(airspaces.map((as, index) => {
        if (index === id) {
          return {
            ...as,
            name: value,
            lower: selectedAirspace.lower,
            upper: selectedAirspace.upper
          };
        }
        return as;
      }));
    }
  };

  const handleGroupChange = (group) => {
    setLoadedPreset(group);
    setAnchorEl(null);
    setShowPresetMessage(true);
  
    if (group) {
      const airspaceGroups = jsonData.groups;
      if (airspaceGroups[group]) {
        const { airspaces: groupAirspaces, lowerFL, upperFL } = airspaceGroups[group];
  
        const expandedAirspaces = groupAirspaces.map((name) => {
          const matchedAirspace = availableAirspaces.find((a) => a.name === name);
  
          return {
            name,
            lower: lowerFL !== undefined ? lowerFL : matchedAirspace?.lower ?? 0,
            upper: upperFL !== undefined ? upperFL : matchedAirspace?.upper ?? 660,
          };
        });
  
        setAirspaces(expandedAirspaces);
      }
    }
  };  

  const handlePresetMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePresetMenuClose = () => {
    setAnchorEl(null);
  };

  const getTimeHHMM = date => date.toISOString().slice(11, 16).replace(":", "");
  const getUTCDayNum = date => (date.getUTCDay() || 7); // 1 = Mon, 7 = Sun
  const getUTCMonth = date => date.getUTCMonth() + 1; // 1 = Jan, 12 = Dec

  const validateForm = async () => {
    const now = new Date();
    const thirtyMinutesFromNow = new Date(now.getTime() + 29 * 60 * 1000);
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const eightHours = 8 * 60 * 60 * 1000;
    if (!callsigns.length || !startDate || !endDate || !priority || !numAircraft || !airspaces.every(as => as.name && as.upper) || !bookingType || !agency || !permeability || !activityType) {
      setErrorMessage('All fields except Event and Remarks are required.');
      return false;
    }
    if (!overrideLimits) {
      if (startDate < thirtyMinutesFromNow) {
        setErrorMessage('Reservation must be made at least 30 minutes in advance.');
        return false;
      }
      if (endDate < thirtyMinutesFromNow) {
        setErrorMessage('Check end time. Minimum reservation length is 30 minutes.');
        return false;
      }
    }
    if (startDate >= endDate) {
      setErrorMessage('Start time must be before end time.');
      return false;
    }
    if (endDate - startDate > eightHours) {
      setErrorMessage('Reservation length cannot be more than 8 hours.');
      return false;
    }
    if (startDate > sevenDaysFromNow) {
      setErrorMessage('Reservation can be booked earliest 7 days in advance.');
      return false;
    }
    const response = await axios.get(`${RESERVATIONS_URL}${selectedLocation}.json`);
    const existingReservations = response.data.filter(res => res.status !== 'cancelled' && res.reservation_id !== reservationId);
    try {
      const conflictingAreas = airspaces.filter(as =>
        existingReservations.some(res =>
          res.areas.some(area =>
            area.area_id === as.name && !(res.end < startDate.getTime() || res.start > endDate.getTime())
          )
        )
      );
      if (conflictingAreas.length > 0) {
        const areaNames = conflictingAreas.map(as => as.name).join(', ');
        setErrorMessage(`${areaNames} not available at the time of your reservation.`);
        return false;
      }
    } catch (error) {
      console.error('Error fetching existing reservations:', error);
      setErrorMessage('Error checking existing reservations.');
      return false;
    }
    // Validate mustBeBookedWith dependencies
    const selectedAreaNames = airspaces.map(as => as.name);
    for (const { name } of airspaces) {
      const required = availableAirspaces.find(a => a.name === name)?.mustBeBookedWith || [];
      const missing = required.filter(r =>
        !selectedAreaNames.includes(r) &&
        !existingReservations.some(res =>
          res.areas.some(a => a.area_id === r) &&
          !(res.end < startDate.getTime() || res.start > endDate.getTime())
        )
      );
      if (missing.length) {
        // Show all required areas, not only missing ones
        setErrorMessage(`Area ${name} must be booked with: ${required.join(', ')}.`);
        return false;
      }
    }

    // Validate activation time
    for (const { name } of airspaces) {
      const airspace = availableAirspaces.find(a => a.name === name);
      const rules = airspace?.activation;
      if (!rules?.length) continue;

      const isValid = [startDate, endDate].every(date => {
        const t = getTimeHHMM(date); // e.g. '0900'
        const d = getUTCDayNum(date); // 1 (Mon) to 7 (Sun)
        const m = getUTCMonth(date); // 1 to 12
        return rules.some(r =>
          r.time[0] <= t && t <= r.time[1] && // inclusive upper limit
          r.weekday.includes(d) &&
          r.month.includes(m)
        );
      });

      if (!isValid) {
        const readableRules = rules.map(r => {
          const wdays = r.weekday.map(d => ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][d - 1]).join(', ');
          const months = r.month.map(m => `${m}`).join(', ');
          return `${r.time[0]}–${r.time[1]} UTC on ${wdays} (months ${months})`;
        }).join('; ');
        setErrorMessage(`Area ${name} is only reservable at: ${readableRules}`);
        return false;
      }
    }

    // 1. maxReservableByOne
    const userActiveReservations = existingReservations.filter(res =>
      (res.status === 'reserved' || res.status === 'pending' || res.status === 'active') &&
      Number(res.creator) === Number(user.cid)
    );
    if (userActiveReservations.length + 1 > maxReservableByOne) {
      setErrorMessage(`You cannot hold more than ${maxReservableByOne} active reservations at a time (local regulation).`);
      return false;
    }

    // 2. maxReservations (global limit across all users)
    const totalActiveReservations = existingReservations.filter(res =>
      res.status === 'reserved' || res.status === 'pending' || res.status === 'active'
    );
    if (totalActiveReservations.length + 1 > maxReservations) {
      setErrorMessage(`The local system limit of ${maxReservations} simultaneous reservations has been reached.`);
      return false;
    }

    // 3. maxAreasInOne
    if (airspaces.length > maxAreasInOne) {
      setErrorMessage(`You cannot reserve more than ${maxAreasInOne} areas in a single reservation (local regulation).`);
      return false;
    }

    // 4. forceVerticalLimits
    if (forceVerticalLimits) {
      for (const as of airspaces) {
        const airspace = availableAirspaces.find(a => a.name === as.name);
        if (!airspace) continue;

        const definedLower = airspace.lower ?? 0;
        const definedUpper = airspace.upper ?? 660;
        const selectedLower = as.lower ?? definedLower;
        const selectedUpper = as.upper ?? definedUpper;

        if (selectedLower < definedLower || selectedUpper > definedUpper) {
          setErrorMessage(
            `Area ${as.name} must stay within vertical limits FL${definedLower}–FL${definedUpper}.`
          );
          return false;
        }
      }
    }

    setErrorMessage('');
    return true;
  };

  const convertUtcToLocal = (utcDate) => {
    if (!utcDate) return null;
    const timezoneOffset = new Date().getTimezoneOffset();
    return new Date(utcDate.getTime() - timezoneOffset * 60000);
  };

  const convertLocalToUtc = (localDate) => {
    if (!localDate) return null;
    const timezoneOffset = localDate.getTimezoneOffset();
    return new Date(localDate.getTime() + timezoneOffset * 60000);
  };

  const handleCreateReservation = async () => {
    const isValid = await validateForm();
    if (isValid) {
      setShowConfirmationDialog(true);
    }
  };

  const handleOpenDraftDialog = async () => {
    if (!selectedLocation) return; // make sure a location is selected
  
    try {
      const response = await fetch(`${DATA_URL}drafts/${selectedLocation}`, {
        credentials: 'include', // send cookies for authentication
        cache: 'no-store',      // optional: avoid caching
      });
  
      if (!response.ok) throw new Error('Failed to fetch drafts');
  
      const data = await response.json();
  
      // Filter drafts created by the current user
      const userDrafts = data.filter(draft =>
        Number(draft.creator) === Number(user.cid)
      );
  
      setDrafts(userDrafts);
      setShowDraftDialog(true);
  
    } catch (error) {
      console.error("Failed to load drafts", error);
    }
  };  

  const handleLoadDraft = (draft) => {
    setCallsigns(draft.callsigns || []);
    setStartDate(new Date(draft.start));
    setEndDate(new Date(draft.end));
    setAirspaces((draft.areas || []).map(a => ({
      name: a.area_id,
      lower: a.lower,
      upper: a.upper
    })));
    setPriority(draft.priority || '');
    setNumAircraft(draft.nbr_acft || 1);
    setRemarks(draft.remarks || '');
    setBookingType(draft.booking_type || '');
    setAgency(draft.agency || '');
    setPermeability(draft.permeability || '');
    setActivityType(draft.activity_type || '');
    setCreator(draft.creator || 0);
    setShowDraftDialog(false);
  };

  const handleDeleteDraft = async (reservationId) => {
    try {
      const res = await fetch(`${DATA_URL}draft/${reservationId}`, { 
        method: 'DELETE', 
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json();
        console.error("Delete failed:", err);
        return;
      }
      setDrafts(drafts.filter(d => d.reservation_id !== reservationId));
    } catch (error) {
      console.error("Failed to delete draft", error);
    }
  };

  const handleSaveReservation = async () => {
    const isValid = await validateForm();
    if (isValid) {
      const reservationData = {
        creator: parseInt(user.cid, 10),
        callsigns,
        booking_type: bookingType,
        agency,
        permeability,
        activity_type: activityType,
        nbr_acft: parseInt(numAircraft, 10),
        priority: parseInt(priority, 10),
        start: startDate.getTime(),
        end: endDate.getTime(),
        remarks,
        status: 'reserved',
        areas: airspaces.map(as => ({
          area_id: as.name,
          lower: parseInt(as.lower, 10),
          upper: parseInt(as.upper, 10)
        }))
      };
    
      try {
        await axios.post(`${DATA_URL}saveDraft/?location=${selectedLocation}`, reservationData, { withCredentials: true });
        onClose();
      } catch (error) {
        console.error('Error creating reservation:', error);
        if (error.response && error.response.data) {
          console.error('Error details:', error.response.data.details);
        }
      }
    }
  };

  const confirmCreateReservation = async () => {
    onClose();
    const airspaceLookup = Object.fromEntries(
      availableAirspaces.map(as => [as.name, as])
    );
    const reservationData = {
      creator: parseInt(user.cid, 10),
      callsigns,
      booking_type: bookingType,
      agency,
      permeability,
      activity_type: activityType,
      nbr_acft: parseInt(numAircraft, 10),
      priority: parseInt(priority, 10),
      event: event || null,
      start: startDate.getTime(),
      end: endDate.getTime(),
      remarks,
      status: 'reserved',
      areas: airspaces.map(as => {
        const matched = airspaceLookup[as.name] || {};
        return {
          area_id: as.name,
          lower: parseInt(as.lower, 10),
          upper: parseInt(as.upper, 10),
          coordinates: matched.coordinates || null,
          foreignClusters: matched.foreignClusters || ["none"],
        };
      })
    };
  
    try {
      await axios.post(`${RESERVATION_CREATE_URL}?location=${selectedLocation}`, reservationData, { withCredentials: true });
      window.location.reload();
    } catch (error) {
      console.error('Error creating reservation:', error);
      if (error.response && error.response.data) {
        console.error('Error details:', error.response.data.details);
      }
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) return;
    const airspaceLookup = Object.fromEntries(
      availableAirspaces.map(as => [as.name, as])
    );
    const reservationData = {
      reservation_id: reservationId,
      creator: parseInt(user.cid, 10),
      callsigns,
      booking_type: bookingType,
      agency,
      permeability,
      activity_type: activityType,
      nbr_acft: parseInt(numAircraft, 10),
      priority: parseInt(priority, 10),
      event: event || null,
      start: startDate.getTime(),
      end: endDate.getTime(),
      remarks,
      areas: airspaces.map(as => {
        const matched = airspaceLookup[as.name] || {};
        return {
          area_id: as.name,
          lower: parseInt(as.lower, 10),
          upper: parseInt(as.upper, 10),
          coordinates: matched.coordinates || null,
          foreignClusters: matched.foreignClusters || [""],
        };
      })
    };
    try {
      const response = await axios.put(`${RESERVATION_UPDATE_URL}/${reservationId}?location=${selectedLocation}`, reservationData, { withCredentials: true });
      console.log('Reservation updated:', response.data);
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error updating reservation:', error);
      if (error.response && error.response.data) {
        console.error('Error details:', error.response.data.details);
      }
    }
  };

  const handleCancel = () => {
    setShowCancelReason(true);
  };

  const handleCancelReservation = async () => {
    if (cancellationReason.length < 3) {
      setErrorMessage('Cancellation reason must be at least 3 characters long.');
      return;
    }
    const reservationData = {
      reservation_id: reservationId,
      remarks: `${cancellationReason}`,
    };
    try {
      const response = await axios.put(`${RESERVATION_CANCEL_URL}/${reservationId}?location=${selectedLocation}`, reservationData, { withCredentials: true });
      console.log('Reservation cancelled:', response.data);
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      if (error.response && error.response.data) {
        console.error('Error details:', error.response.data.details);
      }
    }
  };

  const handleActivateNow = async () => {
    const reservationData = {
      reservation_id: reservationId,
      start: new Date().getTime(),
      status: 'active',
    };
    try {
      const response = await axios.put(`${RESERVATION_ACTIVATE_URL}/${reservationId}?location=${selectedLocation}`, reservationData, { withCredentials: true });
      console.log('Reservation activated:', response.data);
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error activating reservation:', error);
      if (error.response && error.response.data) {
        console.error('Error details:', error.response.data.details);
      }
    }
  };

  return (
    <>
    <Modal open={isOpen} onClose={onClose}>
      <Box style={ModalStyles.overlay}>
        <Box style={ModalStyles.modal}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {mode === 'create'
                ? `NOT_PLANNED Reservation￢CLUSTER_${selectedLocation ? selectedLocation.substring(0, 2).toUpperCase() : ''}`
                : `${reservation?.status.toUpperCase()} Reservation ${reservationId}￢CLUSTER_${selectedLocation ? selectedLocation.substring(0, 2).toUpperCase() : ''}`}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          {mode === 'create' && (
            <Button variant="outlined" color='secondary' onClick={handleOpenDraftDialog} sx={{ borderRadius: 0 }}>
                Load from Draft
            </Button>
          )}
          {!clusterIsActive && (
            <Box mb={2} mt={2}>
              <Alert severity="error">
                {selectedLocation.toUpperCase()} Cluster is currently not operational.
              </Alert>
            </Box>
          )}
          {user?.restricted && (
              <Alert severity="warning">
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Box fontWeight="bold">
                    Your account is restricted. You are not able to add new reservations.
                  </Box>

                  {user?.restriction_reason && (
                      <Box>
                        Reason: {user.restriction_reason}
                      </Box>
                  )}
                </Box>
              </Alert>
          )}
          <Box style={ModalStyles.sectionHeader} className='section-header-bg' onClick={() => toggleSection('basic')}>
            <div>Basic</div>
            <div className='expand-form'>{expandedSections.basic ? '—' : '+'}</div>
          </Box>
          {expandedSections.basic && (
            <Box style={ModalStyles.sectionContent}>
              <Box display="flex" justifyContent="space-between">
                <Box flex={1} mr={1}>
                  <p className='modal-sub-title'>Creator</p>
                  <TextField fullWidth value={mode === 'create' ? user.cid : creator} onChange={() => {}} className='text-fields user-icon' size="small" disabled={view === ''} />
                </Box>
                <Box flex={1} ml={1}>
                  <p className='modal-sub-title'>POC</p>
                  <TextField fullWidth value={mode === 'create' ? user.cid : creator} onChange={() => {}} className='text-fields user-icon' size="small" disabled={view === ''} />
                </Box>
              </Box>
              <p className='modal-sub-title warning-text'>⚠ Your VATSIM CID will be shared with other VATSIM users</p>
              <p className='modal-sub-title'>Callsigns</p>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {callsigns.map((cs) => (
                  <Box key={cs} display="flex" alignItems="center" gap={0} bgcolor="lightgray" padding="2px 0px 2px 6px">
                    <Typography>{cs}</Typography>
                    <IconButton onClick={() => handleCallsignRemove(cs)} size="small" disabled={view === ''}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              <TextField
                style={{ width: '100%', marginTop: '10px', marginBottom: '10px' }}
                placeholder=""
                onBlur={handleCallsignAdd}
                size="small"
                className='text-fields'
                disabled={view === ''}
                onKeyDown={(e) => {
                  if (e.key === ',' || e.key === 'Enter') {
                    handleCallsignAdd(e);
                  }
                }}
              />
              {callsignError && (
                <Box style={{ marginTop: '20px', color: 'red' }}>
                  <Typography>{callsignError}</Typography>
                </Box>
              )}

              <Box>
                <p className='modal-sub-title'>Event</p>
                <Select
                  className='text-fields'
                  fullWidth
                  size="small"
                  value={event?.id || ''}
                  disabled={view === ''}
                  onChange={(e) => {
                    const selected = events.find(ev => ev.id === e.target.value);
                    setEvent(selected ? { id: selected.id, name: selected.name } : null);
                  }}
                  displayEmpty
                >
                  {/* Empty option */}
                  <MenuItem value="">
                    <em style={{color: '#aaa'}}>None</em>
                  </MenuItem>

                  {events
                    .filter(ev =>
                      ev.locations?.includes(selectedLocation) &&
                      ['Planned', 'Confirmed', 'On-going'].includes(ev.status)
                    )
                    .map(ev => (
                      <MenuItem key={ev.id} value={ev.id}>
                        {ev.name}
                      </MenuItem>
                    ))
                  }
                </Select>
              </Box>

            </Box>
          )}
          <Box style={ModalStyles.sectionHeader} className='section-header-bg' onClick={() => toggleSection('activity')}>
            <div>Activity</div>
            <div className='expand-form'>{expandedSections.activity ? '—' : '+'}</div>
          </Box>
          {expandedSections.activity && (
            <Box style={ModalStyles.sectionContent}>
              <Box display="flex" gap={2}>
                <Box display="flex" flexDirection="column" width="100%">
                  <p className='modal-sub-title'>Booking Type</p>
                  <Select className='text-fields' fullWidth value={bookingType} size="small" onChange={(e) => setBookingType(e.target.value)} disabled={mode === 'edit'}>
                    <MenuItem value="Temporary Training Area">Temporary Training Area</MenuItem>
                    <MenuItem value="Temporary Danger Area (D)">Temporary Danger Area (D)</MenuItem>
                    <MenuItem value="Temporary Restricted Area (R)">Temporary Restricted Area (R)</MenuItem>
                    <MenuItem value="CBO">Cross-Border Operations (CBO)</MenuItem>
                    <MenuItem value="AAR">Air-to-Air Refuelling (AAR)</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </Box>

                <Box display="flex" flexDirection="column" width="100%">
                  <p className='modal-sub-title'>Responsible Agency</p>
                  <Select
                    className='text-fields'
                    fullWidth
                    value={agency}
                    size="small"
                    onChange={(e) => setAgency(e.target.value)}
                    disabled={mode === 'edit'}
                  >
                    {agencies.map((ag) => (
                      <MenuItem key={ag.value} value={ag.value}>
                        {ag.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Box display="flex" flexDirection="column" width="100%">
                  <Tooltip
                    placement="top-end"
                    title={
                      <React.Fragment>
                        <p>NON_PERMEABLE: No aircraft are able to enter the ARES</p>
                        <p>PERMEABLE: ATC could, upon certain circumstances, route flights through the ARES</p>
                      </React.Fragment>
                    }
                  >
                    <p className='modal-sub-title'>
                      Permeability
                      <HelpOutlineIcon className='help-icon' />
                    </p>
                  </Tooltip>
                  <Select className='text-fields' fullWidth value={permeability} size="small" onChange={(e) => setPermeability(e.target.value)} disabled={mode === 'edit'}>
                    <MenuItem value="Non Permeable">Non Permeable</MenuItem>
                    <MenuItem value="Permeable">Permeable</MenuItem>
                  </Select>
                </Box>
              </Box>
              <Box display="flex" gap={2} mb={1}>
                <Box display="flex" flexDirection="column" width="100%">
                  <p className='modal-sub-title'>Activity Type</p>
                  <Select className='text-fields' fullWidth value={activityType} size="small" onChange={(e) => setActivityType(e.target.value)} disabled={mode === 'edit'}>
                    <MenuItem value="Military Activity">Military Activity</MenuItem>
                    <MenuItem value="Pilot Training">Pilot Training</MenuItem>
                    <MenuItem value="Air Show">Air Show</MenuItem>
                    <MenuItem value="Parasailing Activity">Parasailing Activity</MenuItem>
                    <MenuItem value="Glider Flying and Recreational Activity">Glider Flying and Recreational Activity</MenuItem>
                    <MenuItem value="Practicing the use of emergency signal equipment">Practicing the use of emergency signal equipment</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </Box>
                <Box display="flex" flexDirection="column" width="100%">
                  <p className='modal-sub-title'>Priority</p>
                  <TextField
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    type="number"
                    disabled={view === ''}
                    className='text-fields spinner-icon'
                    inputProps={{ min: 0, max: 99 }}
                    size="small"
                    sx={{'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { opacity: 1, height: '40px'}}}
                  />
                </Box>
                <Box display="flex" flexDirection="column" width="100%">
                  <p className='modal-sub-title'>Number of Aircraft</p>
                  <TextField
                    value={numAircraft}
                    onChange={(e) => setNumAircraft(e.target.value)}
                    type="number"
                    className='text-fields spinner-icon'
                    inputProps={{ min: 1, max: 20 }}
                    disabled={view === ''}
                    size="small"
                    sx={{'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { opacity: 1, height: '40px'}}}
                  />
                </Box>
              </Box>
            </Box>
          )}
          <Box style={ModalStyles.sectionHeader} className='section-header-bg' onClick={() => toggleSection('time')}>
            <div>Time</div>
            <div className='expand-form'>{expandedSections.time ? '—' : '+'}</div>
          </Box>
          {expandedSections.time && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box style={ModalStyles.sectionContent}>
                <Box display="flex" justifyContent="space-between">
                  {[
                    { label: 'Start', type: 'date', value: startDate, onChange: setStartDate },
                    { label: 'Time UTC', type: 'time', value: startDate, onChange: setStartDate },
                    { label: 'End', type: 'date', value: endDate, onChange: setEndDate },
                    { label: 'Time UTC', type: 'time', value: endDate, onChange: setEndDate },
                  ].map(({ label, type, value, onChange }, idx) => {
                    const Picker = type === 'date' ? DatePicker : TimePicker;
                    const className = type === 'date' ? 'calendar-icon' : 'timepicker-icon';
                    return (
                      <Box key={idx} flex={1} mx={1}>
                        <p className='modal-sub-title'>{label}</p>
                        <Picker
                          value={convertLocalToUtc(value)}
                          disabled={view === ''}
                          onChange={(date) => onChange(convertUtcToLocal(date))}
                          {...(type === 'time' ? { ampm: false } : { format: 'dd/MM/yyyy' })}
                          slots={{ textField: TextField }}
                          slotProps={{
                            textField: {
                              size: 'small',
                              className: `text-fields ${className}`,
                              InputProps: {
                                sx: {
                                  '& .MuiSvgIcon-root': {
                                    color: 'transparent',
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  {['Before Buffer', 'After Buffer'].map((label, idx) => (
                    <Box key={idx} flex={1} mx={1}>
                      <p className="modal-sub-title">{label}</p>
                      <TextField
                        value="00:00"
                        disabled
                        size="small"
                        className="text-fields buffer-icon"
                        InputProps={{
                          sx: {
                            color: 'white',
                          },
                        }}
                        style={{ width: '100%', cursor: 'not-allowed', backgroundColor: '#f8f8f8' }}
                      />
                    </Box>
                  ))}
                </Box>
                {view === 'ATCView' && (
                  <Box display="flex" alignItems="center" mt={2}>
                    <Checkbox
                      checked={overrideLimits}
                      onChange={(e) => setOverrideLimits(e.target.checked)}
                      color="primary"
                      sx={{
                        color: 'gray'
                      }}
                    />
                    <Typography variant="body2" color='gray'>Override Limits</Typography>
                  </Box>
                )}
              </Box>
            </LocalizationProvider>

          )}
          <Box style={{...ModalStyles.sectionHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '44px'}} 
            onClick={() => toggleSection('airspace')} className='section-header-bg'>
            <div>Airspace</div>
            <div className='expand-form'>{expandedSections.airspace ? '—' : '+'}</div>
          </Box>
          {expandedSections.airspace && (
            <Box style={ModalStyles.sectionContent}>
              <Button
                aria-controls="preset-menu"
                aria-haspopup="true"
                onClick={handlePresetMenuOpen}
                endIcon={<ArrowDropDownIcon />}
                style={{color: '#1a475f', fontSize: '0.8rem' }}
                disabled={view === ''}
              >
                Load Preset
              </Button>
              <Box display="flex" flexDirection="column" width="100%">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Menu
                    id="preset-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handlePresetMenuClose}
                  >
                    {jsonData && jsonData.groups
                    ? Object.keys(jsonData.groups).map(group => (
                        <MenuItem key={group} onClick={() => handleGroupChange(group)}>
                          <ListItemText primary={group} />
                        </MenuItem>
                      ))
                    : null}
                  </Menu>
                </Box>
                {showPresetMessage && loadedPreset && (
                  <Typography variant="body2" className='modal-sub-title warning-text' style={{ marginTop: '8px' }}>
                    Airspace group {loadedPreset} loaded
                  </Typography>
                )}
              </Box>
              {airspaces.map((as, idx) => (
                <Box key={idx} display="flex" gap={2} alignItems="end">
                  <Box display="flex" flexDirection="column" width="100%">
                    <p className='modal-sub-title'>Name</p>
                    <Autocomplete
                      options={availableAirspaces}
                      disabled={view === ''}
                      getOptionLabel={(option) => option.name}
                      value={availableAirspaces.find(airspace => airspace.name === as.name) || null}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          handleAirspaceNameChange(idx, newValue.name);
                        }
                      }}
                      renderOption={(props, option) => {
                        const { key, ...rest } = props;
                        return (
                          <Box key={key} component="li" {...rest} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div>{option.name}</div>
                            <div style={{ color: '#a8a8a8', fontSize: '0.8rem' }}>{option.type}</div>
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" className='text-fields' />
                      )}
                      disableClearable
                      freeSolo={false}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" width="300px">
                    <p className='modal-sub-title'>Lower FL</p>
                    <TextField
                      value={as.lower}
                      disabled={view === ''}
                      onChange={(e) => handleAirspaceChange(idx, 'lower', e.target.value)}
                      type="number"
                      size="small"
                      className='text-fields spinner-icon'
                      inputProps={{ min: 0, max: 999, step: 5 }}
                      sx={{'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { opacity: 1, height: '40px'}}}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" width="300px">
                    <p className='modal-sub-title'>Upper FL</p>
                    <TextField
                      value={as.upper}
                      disabled={view === ''}
                      onChange={(e) => handleAirspaceChange(idx, 'upper', e.target.value)}
                      type="number"
                      size="small"
                      className='text-fields spinner-icon'
                      inputProps={{ min: 0, max: 999, step: 5 }}
                      sx={{'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { opacity: 1, height: '40px'}}}
                    />
                  </Box>
                  <Button
                    onClick={() => handleAirspaceDelete(idx)}
                    disabled={airspaces.length === 1 || view === ''}
                    sx={({ palette }) => ({
                      height: '40px',
                      width: '40px',
                      minWidth: '40px',
                      backgroundColor: airspaces.length === 1 ? undefined : '#e48381',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: airspaces.length === 1 ? undefined : '#e64440',
                      },
                    })}
                  >
                    <DeleteIcon />
                  </Button>
                </Box>
              ))}
              <Button onClick={handleAirspaceAdd}
                      disabled={view === ''}
                sx={({ palette }) => ({
                  marginTop: '14px',
                  height: '40px',
                  width: '40px',
                  minWidth: '40px',
                  backgroundColor: '#1976D2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#115293',
                  },
                })}
              >
                <AddIcon />
              </Button>
            </Box>
          )}
          <Box style={ModalStyles.sectionHeader} className='section-header-bg' onClick={() => toggleSection('remarks')}>
            <div>Remarks</div>
            <div className='expand-form'>{expandedSections.remarks ? '—' : '+'}</div>
          </Box>
          {expandedSections.remarks && (
            <Box style={ModalStyles.sectionContent}>
              <p className='modal-sub-title'>Add Remark</p>
              <TextField
                style={{ width: '100%', marginBottom: '10px' }}
                value={remarks}
                disabled={view === ''}
                size="small"
                className='text-fields'
                onChange={(e) => setRemarks(e.target.value.toUpperCase())}
              />
            </Box>
          )}
          {errorMessage && (
            <Box mb={2} mt={2}>
              <Alert severity="error">
                <AlertTitle>Validation Error</AlertTitle>
                {errorMessage}
              </Alert>
            </Box>
          )}
          {showCancelReason && (
            <Box style={{ marginTop: '20px' }}>
              <p className='modal-sub-title'>Cancellation Reason</p>
              <TextField
                style={{ width: '100%' }}
                value={cancellationReason}
                size="small"
                className='text-fields'
                onChange={(e) => setCancellationReason(e.target.value)}
              />
              <Button variant="contained" onClick={handleCancelReservation} style={{ marginTop: '10px', backgroundColor: '#ea5056' }} sx={{ borderRadius: 0 }}>
                Confirm
              </Button>
            </Box>
          )}
            {view !== '' && (
          <Box display="flex" justifyContent="flex-end" gap={1} style={{ marginTop: '20px' }}>
            {mode === 'edit' && view === 'ATCView' && reservation?.status !== 'active' && reservation?.status !== 'cancelled' && (
              <Button variant="contained" onClick={handleActivateNow} style={{ backgroundColor: '#40bb78' }} sx={{ borderRadius: 0 }}>
                Activate Now
              </Button>
            )}
            {mode === 'edit' && view === 'PilotView' && reservation?.status !== 'cancelled' && (
              <Button variant="contained" onClick={handleSubmit} disabled={reservation?.status !== 'reserved'} sx={{ borderRadius: 0 }}>
                Edit Reservation
              </Button>
            )}
            {mode === 'edit' && view === 'ATCView' && reservation?.status !== 'cancelled' && (
              <Button variant="contained" onClick={handleSubmit} sx={{ borderRadius: 0 }}>
                Edit Reservation
              </Button>
            )}
            {mode === 'create' && (
              <Button variant="outlined" color='secondary' onClick={handleSaveReservation} sx={{ borderRadius: 0 }}>
                Save as draft
              </Button>
            )}
            {mode === 'create' && (
              <Button variant="contained" onClick={handleCreateReservation} sx={{ borderRadius: 0 }}>
                Create
              </Button>
            )}
            {mode === 'edit' && reservation?.status !== 'cancelled' && (
              <Button variant="contained" onClick={handleCancel} sx={{ borderRadius: 0 }}>
                Cancel Reservation
              </Button>
            )}
            <Button variant="contained" onClick={onClose} sx={{ borderRadius: 0 }}>
              Close
            </Button>
          </Box>
            )}
          {view === 'ATCView' && (
            <p className='modal-sub-title warning-text' style={{ textAlign: 'right' }}>
              ⚠️ Edit or Activate reservation only if the area is within your AoR
            </p>
          )}
        </Box>
      </Box>
    </Modal>

    {showConfirmationDialog && (
      <Dialog
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 0,
            maxWidth: '400px',
            textAlign: 'center'
          },
        }}
      >
        <DialogTitle>Confirm Area Reservation</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to create an Area Reservation starting at {convertLocalToUtc(startDate).toLocaleString()} UTC.
          </Typography>
          {jsonData?.pilotDocs && (
            <Typography style={{paddingTop: '20px'}}>
              Are you familiar with the <a href={jsonData.pilotDocs} target="_blank" rel="noopener noreferrer">local instructions</a>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '20px', justifyContent: 'center', gap: 10 }}>
          <Button onClick={confirmCreateReservation} variant="contained" color='secondary' sx={{ borderRadius: 0 }}>
            CONFIRM RESERVATION
          </Button>
          <Button onClick={() => setShowConfirmationDialog(false)} variant="contained" sx={{ borderRadius: 0 }}>
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    )}

    <Dialog
      open={showDraftDialog}
      onClose={() => setShowDraftDialog(false)}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 0,
          textAlign: 'center',
          width: '100%',
          maxWidth: '500px'
        },
      }}
    >
      <DialogTitle>Load from Draft</DialogTitle>
      <DialogContent>
        {drafts.length === 0 ? (
          <p>No personal drafts for CLUSTER_{selectedLocation.substring(0, 2).toUpperCase()}</p>
        ) : (
          drafts.map((draft) => (
            <Box key={draft.reservation_id} display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => handleLoadDraft(draft)}
              sx={{ 
                flexGrow: 1, 
                textTransform: 'none', 
                justifyContent: 'flex-start', 
                alignItems: 'flex-start',
                flexDirection: 'column',
                textAlign: 'left'
              }}
            >
              <span>{draft.areas.map(a => a.area_id).join(', ')}</span>
              <span style={{ fontSize: '0.8rem', color: '#666' }}>
                {`Created ${new Date(Number(draft.reservation_id)).toUTCString()}`}
              </span>
            </Button>

              <Button
                onClick={() => handleDeleteDraft(draft.reservation_id)}
                sx={({ palette }) => ({
                  minWidth: '40px',
                  backgroundColor: '#e48381',
                  color: 'white',
                  marginLeft: 1,
                  '&:hover': {
                    backgroundColor: '#e64440',
                  },
                })}
              >
                <DeleteIcon />
              </Button>
            </Box>
          ))
        )}
        <Button variant="contained" onClick={() => setShowDraftDialog(false)} sx={{ borderRadius: 0 }}>
          Close
        </Button>
      </DialogContent>
    </Dialog>

    </>
  );
};

export default ReservationForm;

